<template>
	<div class="raffle-page-layout">
		<div class="raffle-container">
			<h3 class="raffle-label-title" v-if="raffleObj">{{ raffleObj.label }}</h3>
			<h1 class="raffle-title">Raffle Draw</h1>

			<div class="digits-container">
				<div v-for="(digit, index) in displayDigits" :key="index" class="digit-slot">
					<!-- When spinning, show the animated roller -->
					<div v-if="isSpinning && !finishedDigits[index]" class="digit-roller spinning">
						<!-- Create a long strip of repeating numbers for the animation -->
						<div v-for="n in 30" :key="n" class="digit">
							{{ n % 10 }}
						</div>
					</div>

					<!-- When stopped, show a single static digit -->
					<div v-else class="digit static-digit">
						{{ finishedDigits[index] ? digits[index] : displayDigits[index] }}
					</div>
				</div>
			</div>

			<div class="controls">
				<button @click="startDraw" :disabled="isSpinning" class="draw-button">
					{{ hasDrawn ? 'Draw Again' : 'Start Draw' }}
				</button>
				<button @click="resetDraw" :disabled="isSpinning || !hasDrawn" class="reset-button">
					Reset
				</button>
				<button @click="toggleSound" class="sound-button" :class="{ 'sound-off': !soundEnabled }"
					:title="soundEnabled ? 'Click to mute' : 'Click to unmute'">
					<span v-if="soundEnabled">🔊</span>
					<span v-else>🔇</span>
				</button>
				<button @click="toggleSpeed" class="sound-button" :class="{ 'sound-off': !fastEnabled }"
					:title="fastEnabled ? 'Click to go slower' : 'Click to go faster'">
					<span v-if="fastEnabled">🏃</span>
					<span v-else>🚶🏻</span>
				</button>
			</div>

			<div v-if="hasDrawn && !isSpinning" class="winner-announcement">
				<h2 class="winner-announcement h2">Winning Number</h2>
				<div class="winning-number">{{ winningNumber }}</div>
				<div class="winning-name" v-if="drawnNumberNames.length != 0">{{ drawnNumberNames[drawnNumbers.length - 1] }}</div>
			</div>

			<!-- Audio elements -->
			<audio ref="digitSound1" preload="auto">
				<source src="https://assets.mixkit.co/active_storage/sfx/270/270-preview.mp3" type="audio/mpeg">
			</audio>
			<audio ref="digitSound2" preload="auto">
				<source src="https://assets.mixkit.co/active_storage/sfx/270/270-preview.mp3" type="audio/mpeg">
			</audio>
			<audio ref="digitSound3" preload="auto">
				<source src="https://assets.mixkit.co/active_storage/sfx/270/270-preview.mp3" type="audio/mpeg">
			</audio>
			<audio ref="digitSound4" preload="auto">
				<source src="https://assets.mixkit.co/active_storage/sfx/270/270-preview.mp3" type="audio/mpeg">
			</audio>
			<audio ref="finalSound" preload="auto">
				<source src="https://assets.mixkit.co/active_storage/sfx/2013/2013-preview.mp3" type="audio/mpeg">
			</audio>
		</div>

		<div class="drawn-numbers-container" v-if="drawnNumbers.length > 0">
			<h2>Drawn Numbers</h2>
			<ul>
				<li v-for="(number, index) in drawnNumbers" :key="index"><span class="number">{{ number }}</span><br><span class="name">{{ drawnNumberNames[index] }}</span></li>
			</ul>
		</div>
	</div>
</template>

<script setup>
// Note: this was generated from AI with a few tiny edits. No guarantees about correctness or tidiness.

import { ref, computed, onMounted, inject } from 'vue';
import { useRoute } from 'vue-router';
import notify from 'devextreme/ui/notify';
const $http = inject('$http');

// State
const digits = ref([0, 0, 0, 0, 0]);
const displayDigits = ref([0, 0, 0, 0, 0]);
const isSpinning = ref(false);
const hasDrawn = ref(false);
const finishedDigits = ref([false, false, false, false, false]);
const soundEnabled = ref(true);
const fastEnabled = ref(false);
const speeds = [[2000, 3000, 4000, 5000, 6000, 6500], [4000, 6000, 8000, 10000, 12000, 13000]];
const drawnNumbers = ref([]);
const drawnNumberNames = ref([]);

// Get raffle type from route
const raffleType = useRoute().params.raffleType;
const raffleObj = ref(null);

// Audio refs
const digitSound1 = ref(null);
const digitSound2 = ref(null);
const digitSound3 = ref(null);
const digitSound4 = ref(null);
const finalSound = ref(null);

// Computed
const winningNumber = computed(() => {
	return digits.value.join('').padStart(5, '0');
});
const possibleNumbers = computed(() => {
	let ret = [];
	if (!raffleObj.value || !raffleObj.value.submissions || raffleObj.value.submissions.length <= 0) {
		return ret;
	}
	raffleObj.value.submissions.forEach(submission => {
		const submissionNumber = String(submission).trim();
		if (submissionNumber.length === 5 && submissionNumber.match(/^\d+$/)) {
			if (drawnNumbers.value && drawnNumbers.value.includes(submissionNumber)) {
				return;
			}
			ret.push(submissionNumber);
		}
	});
	return ret;
});

// Methods
const startDraw = () => {
	// Generate random winning number
	if (!raffleObj.value || !raffleObj.value.submissions || raffleObj.value.submissions.length <= 0) {
		alert("No submissions found for this raffle. Please try again later.");
		return;
	}
	if (possibleNumbers.value.length <= 0) {
		alert("Not possible to draw any more numbers from this raffle.");
		return;
	}
	// Don't allow drawing if we have mismatched numbers of names/numbers -- i.e. if we are still waiting for the last winner's name
	if (drawnNumberNames.value.length != drawnNumbers.value.length) {
		alert("Please wait before drawing again.");
		return;
	}
	
	isSpinning.value = true;
	finishedDigits.value = [false, false, false, false, false];

	const raffleSubmissionIdx = Math.floor(Math.random() * possibleNumbers.value.length);
	digits.value = String(possibleNumbers.value[raffleSubmissionIdx]).trim().split('').map(Number);
	console.log("Selecting from pool of " + possibleNumbers.value.length + " numbers.");
	// const newDigits = Array(5).fill(0).map(() => Math.floor(Math.random() * 10));
	// digits.value = newDigits;

	// Stagger the stopping of each digit
	setTimeout(() => stopDigit(0), speeds[fastEnabled.value ? 0 : 1][0]);
	setTimeout(() => stopDigit(1), speeds[fastEnabled.value ? 0 : 1][1]);
	setTimeout(() => stopDigit(2), speeds[fastEnabled.value ? 0 : 1][2]);
	setTimeout(() => stopDigit(3), speeds[fastEnabled.value ? 0 : 1][3]);
	setTimeout(() => stopDigit(4), speeds[fastEnabled.value ? 0 : 1][4]);

	// Show result after all digits have stopped
	setTimeout(() => {
		// Automatically post winning number to backend, and get back the winner's name
		$http.post('/raffle/' + raffleObj.value.id + '/draw', {
			winner: winningNumber.value,
		}).then(res => {
			drawnNumberNames.value.push(res.data && res.data.winner_name ? res.data.winner_name : 'Unknown');
		}).catch(err => {
			console.error(err);
			alert("Unable to post winning number - no numbers will be saved.");
			drawnNumberNames.value.push('Unknown');
		});

		isSpinning.value = false;
		hasDrawn.value = true;
		drawnNumbers.value.push(winningNumber.value);
	}, speeds[fastEnabled.value ? 0 : 1][5]);
};

const stopDigit = (index) => {
	finishedDigits.value[index] = true;
	displayDigits.value[index] = digits.value[index];

	// Play sound when digit stops
	if (soundEnabled.value) {
		if (index === 4) {
			// Play final sound for the last digit
			finalSound.value.currentTime = 0;
			finalSound.value.play().catch(e => console.error("Error playing final sound:", e));
		} else {
			// Play regular sound for other digits using separate audio elements
			const digitSounds = [digitSound1.value, digitSound2.value, digitSound3.value, digitSound4.value];
			const sound = digitSounds[index];
			if (sound) {
				sound.currentTime = 0;
				sound.volume = 0.5; // Increase volume to ensure it's audible
				sound.play().catch(e => console.error(`Error playing digit sound ${index}:`, e));
			}
		}
	}
};

const resetDraw = () => {
	// Prompt if we have drawn numbers already
	if (drawnNumbers.value.length > 0) {
		const confirm = window.confirm('Are you sure you want to reset the draw? This will clear all drawn numbers.');
		if (!confirm) return;
	}

	digits.value = [0, 0, 0, 0, 0];
	displayDigits.value = [0, 0, 0, 0, 0];
	hasDrawn.value = false;
	drawnNumbers.value = [];
	drawnNumberNames.value = [];
};

const toggleSound = () => {
	soundEnabled.value = !soundEnabled.value;
};
const toggleSpeed = () => {
	fastEnabled.value = !fastEnabled.value;
}

// Preload sounds
onMounted(async () => {
	// Download raffle object with submissions list from backend
	raffleObj.value = null;
	$http.get('/raffle/type/' + raffleType + '?submissions=1').then(res => {
		raffleObj.value = res.data;
	}).catch(err => {
		console.error(err);
		notify({ message: "Unable to load this raffle. Please try again later.", position: 'center' }, "error", 2000);
	});

	// Set volumes and preload audio
	const digitSounds = [digitSound1.value, digitSound2.value, digitSound3.value, digitSound4.value];

	digitSounds.forEach(sound => {
		if (sound) {
			sound.volume = 0.7; // Increase volume
			sound.load();
		}
	});

	if (finalSound.value) {
		finalSound.value.volume = 0.7;
		finalSound.value.load();
	}

	// Test play and immediately pause to ensure browser allows audio
	const testAndPrepareAudio = (audio) => {
		if (audio) {
			audio.play()
				.then(() => {
					audio.pause();
					audio.currentTime = 0;
				})
				.catch(e => console.error("Error preparing audio:", e));
		}
	};

	// Prepare all audio elements
	setTimeout(() => {
		digitSounds.forEach(testAndPrepareAudio);
		testAndPrepareAudio(finalSound.value);
	}, 1000);
});
</script>

<style lang="scss">
.raffle-page-layout {
	display: flex;
	gap: 2rem;
	align-items: flex-start;
	justify-content: center;
	flex-wrap: wrap;
	padding: 2rem;
}

.raffle-page-layout {
	.raffle-container {
		font-family: 'Arial', sans-serif;
		color: #fff;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 2rem;
		background-color: #1e1e1e;
		border-radius: 1rem;
		box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
		max-width: 600px;
		width: 100%;
		margin: 2em auto 0 auto;
		position: relative;
		z-index: 1;
	}

	.digits-container {
		display: flex;
		gap: 0.5rem;
		margin-bottom: 2rem;
	}

	.digit-slot {
		position: relative;
		width: 80px;
		height: 120px;
		background-color: #2a2a2a;
		border-radius: 0.5rem;
		overflow: hidden;
		box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5);
	}

	.digit-roller {
		position: absolute;
		width: 100%;
		top: 0;
		left: 0;
		animation: rollDigits 0.5s infinite linear;
	}

	.digit {
		width: 100%;
		height: 120px;
		/* Same as digit-slot height */
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 4rem;
		font-weight: bold;
		color: #fff;
		text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
	}

	.static-digit {
		animation: popIn 0.3s ease-out;
	}

	.spinning {
		filter: blur(3px);
	}

	@keyframes rollDigits {
		0% {
			transform: translateY(0);
		}

		100% {
			transform: translateY(-1000px);
			/* Move up by a large amount to ensure continuous motion */
		}
	}

	@keyframes popIn {
		0% {
			transform: scale(0.8);
			opacity: 0.5;
		}

		50% {
			transform: scale(1.1);
		}

		100% {
			transform: scale(1);
			opacity: 1;
		}
	}

	.controls {
		display: flex;
		gap: 1rem;
		margin-bottom: 2rem;
	}

	button {
		padding: 0.75rem 1.5rem;
		font-size: 1rem;
		font-weight: bold;
		border: none;
		border-radius: 0.5rem;
		cursor: pointer;
		transition: all 0.3s ease;
	}

	button:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.draw-button {
		background-color: #ff4081;
		color: white;
	}

	.draw-button:hover:not(:disabled) {
		background-color: #f50057;
		transform: translateY(-2px);
		box-shadow: 0 5px 15px rgba(255, 64, 129, 0.4);
	}

	.reset-button {
		background-color: #424242;
		color: white;
	}

	.reset-button:hover:not(:disabled) {
		background-color: #616161;
		transform: translateY(-2px);
		box-shadow: 0 5px 15px rgba(97, 97, 97, 0.4);
	}

	.sound-button {
		background-color: #2196f3;
		color: white;
		width: 48px;
		padding: 0.75rem 0;
	}

	.sound-button:hover {
		background-color: #1976d2;
		transform: translateY(-2px);
		box-shadow: 0 5px 15px rgba(33, 150, 243, 0.4);
	}

	.sound-button.sound-off {
		background-color: #9e9e9e;
	}

	.sound-button.sound-off:hover {
		background-color: #757575;
		box-shadow: 0 5px 15px rgba(158, 158, 158, 0.4);
	}

	.winner-announcement {
		text-align: center;
		animation: fadeIn 1s ease;
		position: relative;
		background-color: #1e1e1e;
		padding: 1rem;
		border-radius: 0.5rem;
		overflow: hidden;
	}

	.winning-number {
		font-size: 3rem;
		font-weight: bold;
		color: #ff4081;
		margin: 1rem 0;
		letter-spacing: 0.5rem;
		text-shadow: 0 0 10px rgba(255, 64, 129, 0.5);
		position: relative;
		z-index: 1;
	}
	.winning-name {
		font-size: 1.5rem;
		font-style: italic;
		margin: -1rem 0 1rem 0;
		letter-spacing: 0.05rem;
		text-shadow: 0 0 10px rgba(255, 64, 129, 0.5);
		position: relative;
		z-index: 1;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(20px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	/* Add gradient overlays to create fade effect at top and bottom */
	.digit-slot::before,
	.digit-slot::after {
		content: "";
		position: absolute;
		left: 0;
		width: 100%;
		height: 30px;
		z-index: 2;
		pointer-events: none;
	}

	.digit-slot::before {
		top: 0;
		background: linear-gradient(to bottom, rgba(42, 42, 42, 1) 0%, rgba(42, 42, 42, 0) 100%);
	}

	.digit-slot::after {
		bottom: 0;
		background: linear-gradient(to top, rgba(42, 42, 42, 1) 0%, rgba(42, 42, 42, 0) 100%);
	}

	/* Responsive adjustments */
	@media (max-width: 600px) {
		.digit-slot {
			width: 50px;
			height: 80px;
		}

		.digit {
			height: 80px;
			font-size: 2.5rem;
		}

		.winning-number {
			font-size: 2rem;
		}

		h1 {
			font-size: 2rem;
		}

		.controls {
			flex-wrap: wrap;
			justify-content: center;
		}
	}

	.winner-announcement h2 {
		position: relative;
		z-index: 1;
	}

	.drawn-numbers-container {
		font-family: 'Arial', sans-serif;
		color: #fff;
		background-color: #2a2a2a;
		padding: 1.5rem;
		border-radius: 0.5rem;
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
		width: 300px;
		/* Fixed width for the history */
		max-height: 400px;
		/* Limit height and add scroll */
		overflow-y: auto;
	}

	.drawn-numbers-container h2 {
		color: #2196f3;
		/* Match sound button color */
		font-size: 1.5rem;
		margin-top: 0;
		margin-bottom: 1rem;
		text-align: center;
	}

	.drawn-numbers-container ul {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.drawn-numbers-container li {
		background-color: #424242;
		padding: 0.5rem 1rem;
		margin-bottom: 0.5rem;
		border-radius: 0.3rem;
		text-align: center;
		.number {
			font-size: 1.2rem;
			font-weight: bold;
			letter-spacing: 0.1rem;
		}
		.name {
			font-style: italic;
		}
	}

	.raffle-label-title {
		font-size: 1.5rem;
		font-style: italic;
		color: #ff4081;
		text-align: center;
		margin: 0;
	}
	.raffle-title {
		margin-top: 0;
		margin-bottom: 2rem;
		color: #ff4081;
		text-align: center;
		font-size: 3rem;
		font-weight: bold;
		text-shadow: 0 0 10px rgba(255, 64, 129, 0.5);
	}

	/* Responsive adjustments specific to layout */
	@media (max-width: 850px) {
		/* Adjust breakpoint as needed */
		.raffle-page-layout {
			flex-direction: column;
			/* Stack vertically */
			align-items: center;
			/* Center items vertically */
		}
		.drawn-numbers-container {
			width: 90%;
			/* Take more width */
			max-width: 600px;
			/* Match raffle container max-width */
			margin-top: 2rem;
			/* Add space when stacked */
		}
	}
}
</style>